import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import "./CompanyDashboard.css";

const API_URL = "/api";

const PIE_COLORS = [
  "var(--company-primary)",
  "#078b67",
  "#7c3aed",
  "#f59e0b",
  "#e74c3c",
  "#2563eb",
];

const formatCurrency = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatNumber = (value) =>
  new Intl.NumberFormat("es-CO").format(Number(value || 0));

const formatDateTime = (value) => {
  if (!value) return "";

  return new Date(value).toLocaleString("es-CO", {
    dateStyle: "short",
    timeStyle: "short",
  });
};

const getCompanyInfo = () => {
  try {
    return JSON.parse(
      localStorage.getItem("company_info") || "{}"
    );
  } catch {
    return {};
  }
};

function CompanyDashboard() {
  const [period, setPeriod] = useState(30);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const companyInfo = useMemo(getCompanyInfo, []);

  const getHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("company_token")}`,
  });

  const loadDashboard = useCallback(
    async (showLoader = true) => {
      try {
        if (showLoader) {
          setLoading(true);
        } else {
          setRefreshing(true);
        }

        const response = await axios.get(
          `${API_URL}/dashboard/me?days=${period}`,
          {
            headers: getHeaders(),
          }
        );

        setDashboard(response.data);
      } catch (error) {
        console.error(error);

        toast.error(
          error.response?.data?.detail ||
            "No fue posible cargar la información del dashboard."
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [period]
  );

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const sales = dashboard?.sales || {};
  const customers = dashboard?.customers || {};
  const cash = dashboard?.cash || {};
  const receivable = dashboard?.accounts_receivable || {};
  const inventory = dashboard?.inventory || {};
  const charts = dashboard?.charts || {};

  const salesByDay = charts.sales_by_day || [];
  const topProducts = charts.top_products || [];
  const recentActivity = dashboard?.recent_activity || [];
  const lowStockProducts = inventory.low_stock_list || [];

  const dashboardTitle =
    companyInfo.company_name || "Mi empresa";

  if (loading) {
    return (
      <section className="company-dashboard dashboard-loading">
        <div className="dashboard-loader" />
        <p>Cargando información del dashboard...</p>
      </section>
    );
  }

  return (
    <section className="company-dashboard">
      <div className="dashboard-topbar">
        <div>
          <span className="dashboard-eyebrow">
            RESUMEN GENERAL
          </span>

          <h1>Dashboard</h1>

          <p>
            Hola, {dashboardTitle}. Consulta el estado actual de tu negocio.
          </p>
        </div>

        <div className="dashboard-controls">
          <label>
            <span>Periodo</span>

            <select
              value={period}
              onChange={(event) =>
                setPeriod(Number(event.target.value))
              }
            >
              <option value={7}>Últimos 7 días</option>
              <option value={30}>Últimos 30 días</option>
              <option value={90}>Últimos 3 meses</option>
            </select>
          </label>

          <button
            type="button"
            className="dashboard-refresh-btn"
            onClick={() => loadDashboard(false)}
            disabled={refreshing}
          >
            {refreshing ? "Actualizando..." : "↻ Actualizar"}
          </button>
        </div>
      </div>

      <div className="dashboard-period">
        Información desde{" "}
        <strong>{dashboard?.period?.start_date || "-"}</strong>
        {" hasta "}
        <strong>{dashboard?.period?.end_date || "-"}</strong>
      </div>

      <div className="dashboard-kpis">
        <article className="dashboard-kpi-card kpi-sales">
          <div className="kpi-icon">💰</div>

          <div>
            <span>Ingresos netos</span>
            <strong>{formatCurrency(sales.net_sales)}</strong>

            <small
              className={
                Number(sales.comparison_percent) >= 0
                  ? "trend-positive"
                  : "trend-negative"
              }
            >
              {Number(sales.comparison_percent) >= 0 ? "▲" : "▼"}{" "}
              {Math.abs(Number(sales.comparison_percent || 0))}% vs.
              periodo anterior
            </small>
          </div>
        </article>

        <article className="dashboard-kpi-card kpi-orders">
          <div className="kpi-icon">🛒</div>

          <div>
            <span>Ventas realizadas</span>
            <strong>{formatNumber(sales.total_sales)}</strong>
            <small>
              Ticket promedio: {formatCurrency(sales.average_ticket)}
            </small>
          </div>
        </article>

        <article className="dashboard-kpi-card kpi-cash">
          <div className="kpi-icon">🏦</div>

          <div>
            <span>Flujo neto de caja</span>
            <strong>{formatCurrency(cash.net_flow)}</strong>
            <small>
              {cash.is_open
                ? `Caja abierta · Saldo ${formatCurrency(
                    cash.current_balance
                  )}`
                : "No hay una caja abierta"}
            </small>
          </div>
        </article>

        <article className="dashboard-kpi-card kpi-customers">
          <div className="kpi-icon">👥</div>

          <div>
            <span>Clientes activos</span>
            <strong>{formatNumber(customers.active_customers)}</strong>
            <small>
              {formatNumber(customers.new_customers)} nuevos en el período
            </small>
          </div>
        </article>
      </div>

      <div className="dashboard-main-grid">
        <article className="dashboard-panel dashboard-sales-chart">
          <div className="dashboard-panel-header">
            <div>
              <h2>Ventas netas por día</h2>
              <p>Resultado de ventas después de reintegros.</p>
            </div>

            <strong>{formatCurrency(sales.net_sales)}</strong>
          </div>

          <div className="dashboard-chart-container">
            {salesByDay.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart
                  data={salesByDay}
                  margin={{
                    top: 10,
                    right: 10,
                    left: 0,
                    bottom: 0,
                  }}
                >
                  <defs>
                    <linearGradient
                      id="salesGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="var(--company-primary)"
                        stopOpacity={0.38}
                      />
                      <stop
                        offset="95%"
                        stopColor="var(--company-primary)"
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                  </defs>

                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#e7edf3"
                  />

                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#778ba2", fontSize: 12 }}
                  />

                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#778ba2", fontSize: 12 }}
                    tickFormatter={(value) =>
                      new Intl.NumberFormat("es-CO", {
                        notation: "compact",
                        maximumFractionDigits: 1,
                      }).format(value)
                    }
                  />

                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={(label) => `Fecha: ${label}`}
                    contentStyle={{
                      borderRadius: "10px",
                      border: "1px solid #dce7f1",
                    }}
                  />

                  <Area
                    type="monotone"
                    dataKey="total"
                    name="Ventas netas"
                    stroke="var(--company-primary)"
                    strokeWidth={3}
                    fill="url(#salesGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="dashboard-empty-chart">
                No hay ventas para el período seleccionado.
              </div>
            )}
          </div>
        </article>

        <article className="dashboard-panel dashboard-products-chart">
          <div className="dashboard-panel-header">
            <div>
              <h2>Productos más vendidos</h2>
              <p>Unidades vendidas durante el período.</p>
            </div>
          </div>

          <div className="dashboard-chart-container">
            {topProducts.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={topProducts}
                    dataKey="quantity"
                    nameKey="name"
                    cx="50%"
                    cy="47%"
                    innerRadius={62}
                    outerRadius={98}
                    paddingAngle={3}
                  >
                    {topProducts.map((product, index) => (
                      <Cell
                        key={`${product.product_id}-${index}`}
                        fill={PIE_COLORS[index % PIE_COLORS.length]}
                      />
                    ))}
                  </Pie>

                  <Tooltip
                    formatter={(value) =>
                      `${formatNumber(value)} unidades`
                    }
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="dashboard-empty-chart">
                No hay productos vendidos en este período.
              </div>
            )}
          </div>

          <div className="dashboard-product-legend">
            {topProducts.slice(0, 4).map((product, index) => (
              <div
                className="dashboard-legend-item"
                key={`${product.product_id}:${product.sku}:${product.name}`}
              >
                <span
                  style={{
                    background:
                      PIE_COLORS[index % PIE_COLORS.length],
                  }}
                />

                <p title={product.name}>{product.name}</p>

                <strong>
                  {formatNumber(product.quantity)} und.
                </strong>
              </div>
            ))}
          </div>
        </article>
      </div>

      <div className="dashboard-secondary-grid">
        <article className="dashboard-panel dashboard-cash-flow">
          <div className="dashboard-panel-header">
            <div>
              <h2>Flujo de caja</h2>
              <p>Movimientos registrados en efectivo.</p>
            </div>

            <span
              className={
                cash.is_open
                  ? "cash-status-open"
                  : "cash-status-closed"
              }
            >
              {cash.is_open ? "Caja abierta" : "Caja cerrada"}
            </span>
          </div>

          <div className="cash-summary-grid">
            <div>
              <span>Ingresos</span>
              <strong className="cash-income">
                {formatCurrency(cash.income)}
              </strong>
            </div>

            <div>
              <span>Egresos</span>
              <strong className="cash-expense">
                {formatCurrency(cash.expenses)}
              </strong>
            </div>

            <div>
              <span>Saldo actual</span>
              <strong>{formatCurrency(cash.current_balance)}</strong>
            </div>
          </div>
        </article>

        <article className="dashboard-panel dashboard-receivable">
          <div className="dashboard-panel-header">
            <div>
              <h2>Cuentas por cobrar</h2>
              <p>Facturas pendientes de pago.</p>
            </div>
          </div>

          <div className="receivable-total">
            {formatCurrency(receivable.pending_amount)}
          </div>

          <div className="receivable-details">
            <span>
              <strong>
                {formatNumber(receivable.pending_invoices)}
              </strong>{" "}
              facturas pendientes
            </span>

            <span className="receivable-overdue">
              <strong>
                {formatNumber(receivable.overdue_invoices)}
              </strong>{" "}
              vencidas · {formatCurrency(receivable.overdue_amount)}
            </span>
          </div>
        </article>

        <article className="dashboard-panel dashboard-inventory-summary">
          <div className="dashboard-panel-header">
            <div>
              <h2>Estado del inventario</h2>
              <p>Control de existencias y alertas.</p>
            </div>
          </div>

          <div className="inventory-dashboard-values">
            <div>
              <span>Productos activos</span>
              <strong>{formatNumber(inventory.total_products)}</strong>
            </div>

            <div className="inventory-alert-value">
              <span>Stock mínimo</span>
              <strong>
                {formatNumber(inventory.low_stock_products)}
              </strong>
            </div>
          </div>
        </article>
      </div>

      <div className="dashboard-bottom-grid">
        <article className="dashboard-panel dashboard-top-products-list">
          <div className="dashboard-panel-header">
            <div>
              <h2>Ranking de productos</h2>
              <p>Productos con mayor movimiento comercial.</p>
            </div>
          </div>

          <div className="dashboard-ranking-list">
            {topProducts.length > 0 ? (
              topProducts.map((product, index) => (
                <div
                  className="dashboard-ranking-item"
                  key={`${product.product_id}:${product.sku}:${product.name}`}
                >
                  <span className="ranking-position">
                    {index + 1}
                  </span>

                  <div className="ranking-product">
                    <strong>{product.name}</strong>
                    <small>{product.sku}</small>
                  </div>

                  <div className="ranking-total">
                    <strong>{formatCurrency(product.total)}</strong>
                    <small>
                      {formatNumber(product.quantity)} unidades
                    </small>
                  </div>
                </div>
              ))
            ) : (
              <p className="dashboard-empty-message">
                Aún no hay productos vendidos.
              </p>
            )}
          </div>
        </article>

        <article className="dashboard-panel dashboard-low-stock">
          <div className="dashboard-panel-header">
            <div>
              <h2>Alertas de inventario</h2>
              <p>Productos que requieren reposición.</p>
            </div>

            <span className="inventory-alert-badge">
              {formatNumber(inventory.low_stock_products)} alertas
            </span>
          </div>

          <div className="dashboard-low-stock-list">
            {lowStockProducts.length > 0 ? (
              lowStockProducts.map((product) => (
                <div
                  className="dashboard-low-stock-item"
                  key={product.id}
                >
                  <div>
                    <strong>{product.name}</strong>
                    <small>{product.sku}</small>
                  </div>

                  <div>
                    <span>
                      {formatNumber(product.current_stock)} /{" "}
                      {formatNumber(product.minimum_stock)}
                    </span>
                    <small>Actual / mínimo</small>
                  </div>
                </div>
              ))
            ) : (
              <p className="dashboard-success-message">
                ✓ Todos los productos tienen stock suficiente.
              </p>
            )}
          </div>
        </article>
      </div>

      <article className="dashboard-panel dashboard-activity">
        <div className="dashboard-panel-header">
          <div>
            <h2>Actividad reciente</h2>
            <p>Últimos movimientos de ventas e inventario.</p>
          </div>
        </div>

        <div className="dashboard-activity-list">
          {recentActivity.length > 0 ? (
            recentActivity.map((activity, index) => (
              <div
                className="dashboard-activity-item"
                key={`${activity.type}-${index}-${activity.date}`}
              >
                <span
                  className={`activity-icon activity-${activity.type}`}
                >
                  {activity.type === "sale" ? "🛒" : "📦"}
                </span>

                <div>
                  <strong>{activity.title}</strong>
                  <p>{activity.description}</p>
                </div>

                <div className="activity-date">
                  {activity.amount !== null &&
                  activity.amount !== undefined ? (
                    <strong>{formatCurrency(activity.amount)}</strong>
                  ) : null}

                  <small>{formatDateTime(activity.date)}</small>
                </div>
              </div>
            ))
          ) : (
            <p className="dashboard-empty-message">
              No hay movimientos recientes para mostrar.
            </p>
          )}
        </div>
      </article>
    </section>
  );
}

export default CompanyDashboard;